// apps/web/src/offline/OfflineAssetService.js

import {
    STORES,
    dbGet,
    dbPut,
    dbGetAllByIndex,
    dbDelete,
} from './db'

import {
    env,
} from 'configs/EnvironmentConfig'

// ======================================================
// API
// ======================================================

const API_BASE_URL =
    env.API_ENDPOINT_URL

// ======================================================
// CACHE DE OBJECT URLs
//
// Evita criar várias blob: URLs para o mesmo arquivo.
// ======================================================

const objectUrlCache =
    new Map()

// ======================================================
// NORMALIZAR URL
// ======================================================

const normalizeUrl = (
    value,
) => {
    if (!value) {
        return ''
    }

    const url =
        String(
            value,
        ).trim()

    if (!url) {
        return ''
    }

    if (
        url.startsWith(
            'data:',
        ) ||
        url.startsWith(
            'blob:',
        )
    ) {
        return url
    }

    if (
        url.startsWith(
            'http://',
        ) ||
        url.startsWith(
            'https://',
        )
    ) {
        return url
    }

    return `${API_BASE_URL}${url}`
}

// ======================================================
// VERIFICAR SE É UMA MÍDIA QUE PODE SER CACHEADA
// ======================================================

const canCacheUrl = (
    value,
) => {
    if (!value) {
        return false
    }

    const url =
        String(
            value,
        ).trim()

    if (!url) {
        return false
    }

    /*
     * data: já contém o próprio arquivo.
     * blob: é temporário e não deve ser persistido.
     */
    if (
        url.startsWith(
            'data:',
        ) ||
        url.startsWith(
            'blob:',
        )
    ) {
        return false
    }

    return true
}

// ======================================================
// BUSCAR ASSET SALVO
// ======================================================

const getAsset =
    async (
        url,
    ) => {
        if (!url) {
            return null
        }

        return dbGet(
            STORES.ASSETS,
            String(
                url,
            ),
        )
    }

// ======================================================
// SALVAR ASSET
// ======================================================

const saveAsset =
    async ({
        gameId,
        url,
        blob,
        contentType,
    }) => {
        if (
            !gameId ||
            !url ||
            !blob
        ) {
            return null
        }

        const now =
            new Date()
                .toISOString()

        const record = {
            /*
             * IMPORTANTE:
             * usamos a URL ORIGINAL como chave.
             *
             * Exemplo:
             * /uploads/quiz/fundo.jpg
             *
             * Isso permite procurar depois usando
             * exatamente o valor salvo na configuração.
             */
            url:
                String(
                    url,
                ),

            gameId:
                String(
                    gameId,
                ),

            blob,

            contentType:
                contentType ||
                blob.type ||
                'application/octet-stream',

            size:
                Number(
                    blob.size ||
                    0,
                ),

            updatedAt:
                now,
        }

        await dbPut(
            STORES.ASSETS,
            record,
        )

        return record
    }

// ======================================================
// BAIXAR E ARMAZENAR UM ASSET
// ======================================================

const cacheAsset =
    async (
        gameId,
        url,
    ) => {
        if (
            !gameId ||
            !canCacheUrl(
                url,
            )
        ) {
            return null
        }

        const originalUrl =
            String(
                url,
            )

        const absoluteUrl =
            normalizeUrl(
                originalUrl,
            )

        if (!absoluteUrl) {
            return null
        }

        try {
            const response =
                await window.fetch(
                    absoluteUrl,
                    {
                        method:
                            'GET',

                        cache:
                            'no-store',
                    },
                )

            if (
                !response.ok
            ) {
                throw new Error(
                    `HTTP ${response.status}`,
                )
            }

            const blob =
                await response.blob()

            if (
                !blob ||
                blob.size <= 0
            ) {
                throw new Error(
                    'ASSET_EMPTY_BLOB',
                )
            }

            const contentType =
                response.headers.get(
                    'content-type',
                ) ||
                blob.type ||
                'application/octet-stream'

            const saved =
                await saveAsset({
                    gameId,
                    url:
                        originalUrl,
                    blob,
                    contentType,
                })

            console.log(
                '[OFFLINE ASSET] Arquivo armazenado:',
                {
                    gameId,

                    url:
                        originalUrl,

                    contentType,

                    size:
                        blob.size,
                },
            )

            return saved
        } catch (
            error
        ) {
            /*
             * Um asset com problema não deve impedir
             * o restante do Quiz de ser preparado.
             */
            console.warn(
                '[OFFLINE ASSET] Não foi possível armazenar:',
                originalUrl,
                error,
            )

            return null
        }
    }

// ======================================================
// CACHEAR VÁRIOS ASSETS
// ======================================================

const cacheAssets =
    async (
        gameId,
        urls,
    ) => {
        if (
            !gameId
        ) {
            return {
                total:
                    0,

                cached:
                    0,

                failed:
                    0,
            }
        }

        const uniqueUrls =
            Array.from(
                new Set(
                    (
                        Array.isArray(
                            urls,
                        )
                            ? urls
                            : []
                    )
                        .filter(
                            Boolean,
                        )
                        .map(
                            (
                                value,
                            ) =>
                                String(
                                    value,
                                ),
                        ),
                ),
            )

        let cached =
            0

        let failed =
            0

        /*
         * Fazemos sequencialmente de propósito.
         *
         * Evita disparar dezenas de downloads
         * simultaneamente em internet lenta de evento.
         */
        for (
            const url
            of uniqueUrls
        ) {
            if (
                !canCacheUrl(
                    url,
                )
            ) {
                continue
            }

            const result =
                await cacheAsset(
                    gameId,
                    url,
                )

            if (result) {
                cached +=
                    1
            } else {
                failed +=
                    1
            }
        }

        console.log(
            '[OFFLINE ASSET] Preparação concluída:',
            {
                gameId,

                total:
                    uniqueUrls
                        .length,

                cached,

                failed,
            },
        )

        return {
            total:
                uniqueUrls
                    .length,

            cached,

            failed,
        }
    }

// ======================================================
// OBTER URL LOCAL
// ======================================================

const getOfflineUrl =
    async (
        url,
    ) => {
        if (!url) {
            return ''
        }

        const originalUrl =
            String(
                url,
            )

        /*
         * data: e blob: já podem ser usados diretamente.
         */
        if (
            originalUrl.startsWith(
                'data:',
            ) ||
            originalUrl.startsWith(
                'blob:',
            )
        ) {
            return originalUrl
        }

        /*
         * Se já criamos uma Object URL para esse asset,
         * reutilizamos.
         */
        if (
            objectUrlCache.has(
                originalUrl,
            )
        ) {
            return objectUrlCache.get(
                originalUrl,
            )
        }

        try {
            const asset =
                await getAsset(
                    originalUrl,
                )

            if (
                !asset?.blob
            ) {
                return ''
            }

            const objectUrl =
                URL.createObjectURL(
                    asset.blob,
                )

            objectUrlCache.set(
                originalUrl,
                objectUrl,
            )

            return objectUrl
        } catch (
            error
        ) {
            console.warn(
                '[OFFLINE ASSET] Erro ao recuperar asset:',
                originalUrl,
                error,
            )

            return ''
        }
    }

// ======================================================
// RESOLVER URL
//
// PRIORIDADE:
// 1. data: / blob:
// 2. asset salvo no IndexedDB
// 3. URL normal da API
//
// Dessa forma o jogo continua usando a mídia local mesmo
// quando navigator.onLine === true, mas a API está fora.
// ======================================================

const resolveUrl =
    async (
        url,
    ) => {
        if (!url) {
            return ''
        }

        const originalUrl =
            String(
                url,
            ).trim()

        if (!originalUrl) {
            return ''
        }

        // ==============================================
        // DATA / BLOB
        // ==============================================

        if (
            originalUrl.startsWith(
                'data:',
            ) ||
            originalUrl.startsWith(
                'blob:',
            )
        ) {
            return originalUrl
        }

        // ==============================================
        // PRIORIDADE: INDEXEDDB
        //
        // Não usamos navigator.onLine aqui.
        //
        // O computador pode continuar "online"
        // mesmo quando a API estiver inacessível.
        // ==============================================

        try {
            const offlineUrl =
                await getOfflineUrl(
                    originalUrl,
                )

            if (
                offlineUrl
            ) {
                console.log(
                    '[OFFLINE ASSET] Usando mídia local:',
                    {
                        original:
                            originalUrl,

                        resolved:
                            offlineUrl,
                    },
                )

                return offlineUrl
            }
        } catch (
            error
        ) {
            console.warn(
                '[OFFLINE ASSET] Falha ao resolver mídia local:',
                originalUrl,
                error,
            )
        }

        // ==============================================
        // NÃO EXISTE LOCALMENTE
        //
        // Tenta a URL normal da API.
        // ==============================================

        const onlineUrl =
            normalizeUrl(
                originalUrl,
            )

        console.log(
            '[OFFLINE ASSET] Mídia não está no cache. Usando API:',
            {
                original:
                    originalUrl,

                resolved:
                    onlineUrl,
            },
        )

        return onlineUrl
    }

// ======================================================
// LISTAR ASSETS DE UM JOGO
// ======================================================

const getGameAssets =
    async (
        gameId,
    ) => {
        if (!gameId) {
            return []
        }

        return dbGetAllByIndex(
            STORES.ASSETS,
            'gameId',
            String(
                gameId,
            ),
        )
    }

// ======================================================
// VERIFICAR SE ASSET EXISTE
// ======================================================

const hasAsset =
    async (
        url,
    ) => {
        const asset =
            await getAsset(
                url,
            )

        return Boolean(
            asset?.blob,
        )
    }

// ======================================================
// REMOVER ASSET
// ======================================================

const removeAsset =
    async (
        url,
    ) => {
        if (!url) {
            return
        }

        const originalUrl =
            String(
                url,
            )

        const objectUrl =
            objectUrlCache.get(
                originalUrl,
            )

        if (
            objectUrl
        ) {
            URL.revokeObjectURL(
                objectUrl,
            )

            objectUrlCache.delete(
                originalUrl,
            )
        }

        await dbDelete(
            STORES.ASSETS,
            originalUrl,
        )
    }

// ======================================================
// LIBERAR OBJECT URLs DA MEMÓRIA
//
// Isso NÃO apaga o IndexedDB.
// ======================================================

const revokeObjectUrls =
    () => {
        for (
            const objectUrl
            of objectUrlCache.values()
        ) {
            try {
                URL.revokeObjectURL(
                    objectUrl,
                )
            } catch (
                error
            ) {
                // Ignorar.
            }
        }

        objectUrlCache.clear()
    }

// ======================================================
// EXPORT
// ======================================================

const OfflineAssetService = {
    normalizeUrl,
    canCacheUrl,

    getAsset,
    saveAsset,

    cacheAsset,
    cacheAssets,

    getOfflineUrl,
    resolveUrl,

    getGameAssets,
    hasAsset,
    removeAsset,

    revokeObjectUrls,
}

export default OfflineAssetService